import AppLayout from "@/layout/AppLayout.vue";
import { setActuallRoute } from "@/router/routeManager";
import { useAuthStore } from "@/stores/auth";
import { CustomerRole } from "@/types/customerRoles";
import { createRouter, createWebHistory } from "vue-router";

/**
 * Meta properties used in route definitions:
 * @property {boolean} requiresAuth - Whether the route requires authentication
 * @property {boolean} requiresCompany - Whether the route requires user to be associated with a company
 * @property {boolean} hiddenInTopBar - Whether to hide this route in the top navigation bar
 * @property {boolean} hiddenInSelfPage - Whether to hide this route in the top navigation bar when it is the current page
 * @property {CustomerRole} minimumCustomerRole - Minimum role required to access this route
 */

const routes = [
  {
    path: "/",
    component: AppLayout,
    children: [
      {
        path: "/",
        name: "login",
        component: () => import("@/views/pages/Auth.vue"),
        meta: { requiresAuth: false, hiddenInTopBar: true },
      },
      {
        path: "/auth/verify-email",
        name: "verify-email",
        component: () => import("@/views/pages/auth/VerifyEmail.vue"),
        meta: { requiresAuth: false, hiddenInTopBar: true },
      },
      {
        path: "/auth/reset-password",
        name: "reset-password",
        component: () => import("@/views/pages/auth/ResetPassword.vue"),
        meta: { requiresAuth: false, hiddenInTopBar: true },
      },
      {
        path: "/map",
        name: "map",
        // Lazy like every other route. Imported statically it pulled Map.vue
        // -> AssetMap.vue -> maplibre-gl (1 MB of sources) into the entry
        // chunk, so every page - login included - downloaded the map engine.
        component: () => import("@/views/Map.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: true,
          hiddenInSelfPage: true,
          hiddenInTopBar: false,
          minimumCustomerRole: CustomerRole.USER,
        },
      },
      {
        path: "/asset-preview/:node_id/:asset_id/:device_id",
        name: "asset-preview",
        props: true,
        component: () => import("@/views/AssetPreview/AssetPreview.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: true,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
        },
      },
      {
        path: "/telemetry/:node_id/:asset_id/:device_id",
        name: "telemetry",
        props: true,
        component: () => import("@/views/Telemetry.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: true,
          minimumCustomerRole: CustomerRole.USER,
        },
      },
      {
        path: "/company",
        name: "company",
        component: () => import("@/views/Company/CompanyManagement.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: true,
          minimumCustomerRole: CustomerRole.OWNER,
          hiddenInSelfPage: true,
          hiddenInTopBar: true,
          allowSecondaryTopBarGoToMap: true, // Allow the rendering of 'Go to Map' button in the secondary top bar
        },
      },
      {
        path: "/notifications",
        name: "notifications",
        component: () => import("@/views/Notifications.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: true,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInSelfPage: true,
          hiddenInTopBar: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
      {
        path: "/notfound",
        name: "notfound",
        component: () => import("@/views/pages/NotFound.vue"),
        meta: {
          hiddenInTopBar: true,
        },
      },
      {
        path: "/error",
        name: "error",
        component: () => import("@/views/pages/auth/Error.vue"),
        meta: {
          hiddenInTopBar: true,
        },
      },
      {
        path: "/no-company",
        name: "no-company",
        component: () => import("@/views/pages/NoCompany.vue"),
        meta: {
          requiresAuth: true,
          hiddenInTopBar: true,
        },
      },
      {
        path: "/unauthorized",
        name: "unauthorized",
        component: () => import("@/views/pages/Unauthorized.vue"),
        meta: {
          requiresAuth: true,
          hiddenInTopBar: true,
        },
      },
      {
        path: "/asset-not-found",
        name: "asset-not-found",
        component: () => import("@/views/pages/AssetNotFound.vue"),
        meta: {
          minimumCustomerRole: CustomerRole.UNKNOWN,
          requiresAuth: true,
          hiddenInTopBar: true,
        },
      },
      {
        path: ":pathMatch(.*)*",
        redirect: "/notfound",
        meta: {
          hiddenInTopBar: true,
        },
      },
      {
        path: "/admin/companies",
        name: "admin-companies",
        component: () => import("@/views/Admin/AdminCompaniesList.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false, // Admin might not be in a company initially or works across them
          minimumCustomerRole: CustomerRole.USER, // Platform Admin role protection handled by requiresAdmin
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true, // Allow the rendering of 'Go to Map' button in the secondary top bar
        },
      },
      {
        path: "/admin/users",
        name: "admin-users",
        component: () => import("@/views/Admin/AdminUsers.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
      {
        path: "/admin/service",
        name: "admin-service",
        component: () => import("@/views/Admin/AdminService.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
      {
        path: "/admin/device-registry",
        name: "admin-device-registry",
        component: () => import("@/views/Admin/AdminDeviceRegistry.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
      {
        path: "/admin/company/create",
        name: "admin-company-create",
        component: () => import("@/views/Admin/AdminCompanyCreate.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: true,
          requiresAdmin: true,
        },
      },
      {
        path: "/admin/company/edit/:id",
        name: "admin-company-edit",
        component: () => import("@/views/Admin/AdminCompanyEdit.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: true,
          requiresAdmin: true,
        },
      },
      {
        path: "/admin/company/modify-owners/:id",
        name: "admin-company-modify-owners",
        component: () => import("@/views/Admin/AdminCompanyModifyOwners.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: true,
          requiresAdmin: true,
        },
      },
      {
        path: "/admin/instructions",
        name: "admin-instructions",
        component: () => import("@/views/Admin/AdminInstructions.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
      {
        path: "/admin/instructions/tutorial/:tutorialId",
        name: "admin-tutorial",
        component: () => import("@/views/Admin/tutorials/TutorialPage.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: true,
          requiresAdmin: true,
        },
      },
      {
        path: "/admin/root",
        name: "admin-root",
        component: () => import("@/views/Admin/AdminRoot.vue"),
        meta: {
          requiresAuth: true,
          requiresCompany: false,
          minimumCustomerRole: CustomerRole.USER,
          hiddenInTopBar: false,
          requiresAdmin: true,
          allowSecondaryTopBarGoToMap: true,
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

// Updated navigation guard
router.beforeEach(async (to, from, next) => {
  const store = useAuthStore();

  // Initialize auth if needed
  if (!store.isLoggedIn) {
    await store.initAuth();
  }

  // If the route requires auth and user is not logged in
  if (to.meta.requiresAuth && !store.isLoggedIn) {
    // Redirect to login
    next({ name: "login" });
    return;
  }

  // If going to login page and already logged in
  if (to.name === "login" && store.isLoggedIn) {
    // Check if user has company before redirecting to map
    const hasCompany = store.hasEffectiveCompany;
    if (!hasCompany) {
      // Ensure company data is loaded
      await store.getCompany();
      if (!store.hasEffectiveCompany) {
        next({ name: "no-company" });
        return;
      }
    }
    // Redirect to map or home page
    next({ name: "map" });
    return;
  }

  // Handle no-company route - redirect to map if user has a company
  if (to.name === "no-company") {
    const hasCompany = store.hasEffectiveCompany;
    if (hasCompany) {
      next({ name: "map" });
      return;
    }
    // If no company, allow access to no-company page
    next();
    if (to.name) {
      setActuallRoute(to.name);
    }
    return;
  }

  // Skip company check for specific routes that don't require it
  if (
    to.name === "login" ||
    to.name === "verify-email" ||
    to.name === "reset-password" ||
    to.name === "error" ||
    to.name === "notfound" ||
    to.name === "unauthorized"
  ) {
    next();
    if (to.name) {
      setActuallRoute(to.name);
    }
    return;
  }

  // Check company access for authenticated routes
  if (to.meta.requiresAuth && store.isLoggedIn) {
    const hasCompany = store.hasEffectiveCompany;
    if (!hasCompany) {
      // Ensure company data is loaded
      await store.getCompany();
      if (!store.hasEffectiveCompany && to.meta.requiresCompany) {
        next({ name: "no-company" });
        return;
      }
    }
  }

  // Check for admin requirement
  if (to.meta.requiresAdmin) {
    // We access the property directly as it is a computed/ref in the store
    if (!store.isAdmin) {
      console.log(
        "Page requires admin privileges, but user is not an admin. Redirecting to unauthorized.",
      );
      next({ name: "unauthorized" });
      return;
    }

    // Admin-only routes do not depend on company membership customer roles.
    next();
    if (to.name) {
      setActuallRoute(to.name);
    }
    return;
  }

  // If the route does not have a minimum customer role, protect it
  if (!to.meta.minimumCustomerRole && to.meta.requiresAuth === true) {
    console.log(
      `Page ${to.name} does not have a minimum customer role, protecting route`,
    );
    console.error("Redirect to notfound due to missing minimum customer role");
    next({ name: "notfound" });
    return;
  }

  // Check if the user has the minimum role
  if (store.isAdmin && store.isImpersonatingCompany) {
    next();
    if (to.name) {
      setActuallRoute(to.name);
    }
    return;
  }

  const userRole = await store.getUserRole();
  if (!userRole) {
    console.log("No customer role found");
    next({ name: "notfound" });
    return;
  }

  // Check if user has the minimum role
  if (
    Object.values(CustomerRole).indexOf(userRole) <
    Object.values(CustomerRole).indexOf(to.meta.minimumCustomerRole)
  ) {
    console.log(
      "User does not have the minimum role",
      userRole,
      to.meta.minimumCustomerRole,
    );
    next({ name: "notfound" });
    return;
  }

  // Allow navigation
  next();
  if (to.name) {
    setActuallRoute(to.name);
  }
});

export default router;
